import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Download, Copy, Check, ShieldAlert, KeyRound, FileArchive } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin-only page to reliably fetch the Android release keystore + its base64.
 * This exists because copy-pasting a 3680-char blob through chat UIs frequently
 * truncates. From here you either click "Download .jks" and base64 it locally,
 * or click "Copy base64" and paste directly into GitHub secret.
 */
export default function MobileSigning() {
  const [status, setStatus] = useState(null);
  const [b64, setB64] = useState("");
  const [copied, setCopied] = useState(false);
  const [loadingB64, setLoadingB64] = useState(false);

  useEffect(() => {
    api.get("/mobile-signing/status").then(({ data }) => setStatus(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, []);

  async function loadBase64() {
    setLoadingB64(true);
    try {
      const { data } = await api.get("/mobile-signing/keystore-base64", { responseType: "text" });
      setB64(typeof data === "string" ? data : String(data));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoadingB64(false); }
  }

  async function copyBase64() {
    if (!b64) await loadBase64();
    const text = b64 || document.querySelector('[data-testid="ms-base64-text"]')?.value || "";
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(`Copied ${text.length} chars to clipboard`);
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      toast.error("Clipboard blocked — select the text and copy manually");
    }
  }

  function downloadJks() {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/mobile-signing/keystore.jks`;
    // Session cookie carries auth
    window.open(url, "_blank");
  }

  const password = "ALLEUR2arBtMEKqdXpT83az3ZZawhiYh"; // Same for keystore + key
  const alias = status?.key_alias || "closet-release";

  return (
    <div className="p-6 sm:p-8 max-w-5xl">
      <div className="brut-badge mb-4">MOBILE SIGNING · ADMIN ONLY</div>
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mb-2">
        Android release keystore
      </h1>
      <p className="text-zinc-600 max-w-2xl mb-6">
        The signing key GitHub Actions uses to produce your production APK. Never commit these
        values to code. Paste them into <b>GitHub → Settings → Secrets and variables → Actions</b>.
      </p>

      {status === null && (
        <div className="brut-card-flat p-4">Loading…</div>
      )}

      {status && !status.present && (
        <div className="brut-card-flat p-4 bg-[#FDECE0] flex items-start gap-3">
          <ShieldAlert size={20} className="text-[#B34A00] shrink-0 mt-0.5" />
          <div>
            <div className="font-bold">No keystore on disk.</div>
            <div className="text-sm text-zinc-700">Ask the developer to regenerate it — it should live at <code>/app/mobile-signing/</code>.</div>
          </div>
        </div>
      )}

      {status?.present && (
        <>
          {/* Fingerprint / integrity card */}
          <div className="brut-card-flat p-5 mb-6 bg-[#F8F1E4]">
            <div className="flex items-center gap-2 mb-3">
              <KeyRound size={16} />
              <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">Integrity</div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              <Field label="JKS size">{status.jks_bytes} bytes</Field>
              <Field label="Base64 length">{status.base64_length} chars</Field>
              <Field label="Key alias">{alias}</Field>
              <Field label="SHA-1 fingerprint">
                <code className="break-all text-[11px]">{status.sha1_fingerprint}</code>
              </Field>
              <Field label="Base64 SHA-256" wide>
                <code className="break-all text-[11px]">{status.base64_sha256}</code>
              </Field>
            </div>
          </div>

          {/* Action buttons */}
          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            <button
              onClick={downloadJks}
              className="brut-card-flat p-5 text-left hover:bg-[#F3E9DA] transition-colors group"
              data-testid="ms-download-jks-btn"
            >
              <FileArchive size={20} className="mb-2 text-[#7A3E1F]" />
              <div className="font-display font-black text-lg mb-1">Download .jks</div>
              <div className="text-sm text-zinc-600 mb-2">The raw binary keystore file.</div>
              <div className="inline-flex items-center gap-1 text-xs font-mono-ds font-bold text-[#7A3E1F] group-hover:underline">
                <Download size={12} /> closet-release.jks
              </div>
            </button>

            <button
              onClick={copyBase64}
              className="brut-card-flat p-5 text-left hover:bg-[#F3E9DA] transition-colors group"
              data-testid="ms-copy-base64-btn"
            >
              {copied ? <Check size={20} className="mb-2 text-[#22A06B]" /> : <Copy size={20} className="mb-2 text-[#7A3E1F]" />}
              <div className="font-display font-black text-lg mb-1">
                {copied ? "Copied ✓" : loadingB64 ? "Loading…" : "Copy base64"}
              </div>
              <div className="text-sm text-zinc-600 mb-2">Full 3680-char string, one line.</div>
              <div className="inline-flex items-center gap-1 text-xs font-mono-ds font-bold text-[#7A3E1F] group-hover:underline">
                Paste into ANDROID_KEYSTORE_BASE64
              </div>
            </button>
          </div>

          {/* Inline textarea fallback — if clipboard is blocked, user can select-all + copy */}
          <div className="brut-card-flat p-5 mb-6">
            <div className="flex items-center justify-between mb-3">
              <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500">Base64 fallback (manual select)</div>
              {!b64 && (
                <button onClick={loadBase64} className="brut-btn-outline px-3 py-1 text-xs" data-testid="ms-load-base64-btn">
                  Load
                </button>
              )}
            </div>
            {b64 ? (
              <textarea
                readOnly
                value={b64}
                onFocus={(e) => e.target.select()}
                data-testid="ms-base64-text"
                className="w-full h-40 font-mono text-[10px] leading-tight p-3 border-2 border-black bg-white break-all resize-y"
              />
            ) : (
              <div className="text-sm text-zinc-500">Click <b>Load</b> to fetch the raw base64 into a selectable text box.</div>
            )}
          </div>

          {/* Credentials card */}
          <div className="brut-card-flat p-5 mb-6 bg-[#FDF6EE]">
            <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-3">Other 3 GitHub secrets · copy each verbatim</div>
            <div className="space-y-2">
              <SecretRow name="ANDROID_KEYSTORE_PASSWORD" value={password} />
              <SecretRow name="ANDROID_KEY_ALIAS" value={alias} />
              <SecretRow name="ANDROID_KEY_PASSWORD" value={password} />
            </div>
          </div>

          {/* Playbook */}
          <div className="brut-card-flat p-5">
            <div className="font-display font-black text-lg mb-3">How to use</div>
            <ol className="space-y-2 text-sm list-decimal list-inside text-zinc-800">
              <li>Open <code>github.com/&lt;you&gt;/&lt;repo&gt;/settings/secrets/actions</code></li>
              <li>Create secret <code>ANDROID_KEYSTORE_BASE64</code> → paste from the <b>Copy base64</b> button</li>
              <li>Create the other 3 secrets (values above)</li>
              <li>Actions tab → run <b>Build Android APK</b> → download <code>app-release.apk</code></li>
            </ol>
            <div className="mt-4 text-xs text-zinc-500 border-l-2 border-[#7A3E1F] pl-3">
              Save the password in your password manager. If lost, you cannot ship updates to the same
              install without users uninstalling.
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Field({ label, children, wide }) {
  return (
    <div className={wide ? "sm:col-span-2" : ""}>
      <div className="font-mono-ds text-[10px] tracking-widest uppercase text-zinc-500 mb-1">{label}</div>
      <div className="font-medium">{children}</div>
    </div>
  );
}

function SecretRow({ name, value }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* noop */ }
  }
  return (
    <div className="flex items-center gap-3 p-2 border-2 border-black bg-white">
      <div className="font-mono-ds text-[11px] font-bold w-56 shrink-0">{name}</div>
      <code className="flex-1 text-xs break-all">{value}</code>
      <button onClick={copy} className="shrink-0 brut-btn-outline px-2 py-1 text-xs inline-flex items-center gap-1" data-testid={`ms-copy-${name.toLowerCase()}-btn`}>
        {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
      </button>
    </div>
  );
}
